import { useEffect, useRef, forwardRef, ReactNode, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useAppStore } from '@store'

const ConfirmationSaveStore = forwardRef<
  HTMLDivElement,
  {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    nombre: string
    mode?: 'success' | 'confirm'
    message?: ReactNode
    autoCloseMs?: number
  }
>(({ isOpen, onClose, onConfirm, nombre, mode = 'confirm', message, autoCloseMs }, ref) => {
  const t = useAppStore.use.t()
  const noBtnRef = useRef<HTMLButtonElement>(null)
  const shouldPreventCloseRef = useRef(false)
  const shouldPreventClose = mode === 'success' && autoCloseMs === undefined
  const [internalOpen, setInternalOpen] = useState(() => {
    if (isOpen && mode === 'success' && autoCloseMs === undefined) {
      return true
    }
    return isOpen
  })
  const allowCloseRef = useRef(false)

  if (shouldPreventClose) {
    shouldPreventCloseRef.current = true
  }

  useEffect(() => {
    if (isOpen) {
      allowCloseRef.current = false
      setInternalOpen(true)
      if (shouldPreventClose) {
        shouldPreventCloseRef.current = true
      }
    } else {
      setInternalOpen(false)
    }
  }, [isOpen, shouldPreventClose])

  useEffect(() => {
    shouldPreventCloseRef.current = shouldPreventClose
  }, [shouldPreventClose])

  useEffect(() => {
    if (isOpen && shouldPreventClose && internalOpen) {
      if (!internalOpen) {
        setInternalOpen(true)
      }
    }
  }, [isOpen, shouldPreventClose, internalOpen])

  useEffect(() => {
    if (isOpen && mode === 'confirm' && noBtnRef.current) {
      noBtnRef.current.focus()
    }
  }, [isOpen, mode])

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    if (isOpen && mode === 'success' && autoCloseMs !== undefined) {
      timer = setTimeout(() => {
        onClose()
      }, autoCloseMs)
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [isOpen, mode, onClose, autoCloseMs])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (shouldPreventCloseRef.current && !allowCloseRef.current) {
        setTimeout(() => {
          setInternalOpen(true)
        }, 0)
        return
      }
      setInternalOpen(false)
      onClose()
    } else {
      setInternalOpen(true)
    }
  }

  const handleCloseButton = () => {
    allowCloseRef.current = true
    setInternalOpen(false)
    onClose()
  }

  return (
    <Dialog.Root
      open={internalOpen}
      onOpenChange={handleOpenChange}
      modal={true}
    >
      <Dialog.Portal>
        <Dialog.Overlay className='z-90 fixed inset-0 bg-black bg-opacity-30' />
        <Dialog.Content
          ref={ref}
          onInteractOutside={(e) => {
            if (shouldPreventCloseRef.current && !allowCloseRef.current) {
              e.preventDefault()
              e.stopPropagation()
              setTimeout(() => {
                setInternalOpen(true)
              }, 0)
            }
          }}
          onPointerDownOutside={(e) => {
            if (shouldPreventCloseRef.current && !allowCloseRef.current) {
              e.preventDefault()
              e.stopPropagation()
              setTimeout(() => {
                setInternalOpen(true)
              }, 0)
            }
          }}
          onEscapeKeyDown={(e) => {
            if (shouldPreventCloseRef.current && !allowCloseRef.current) {
              e.preventDefault()
              e.stopPropagation()
              setTimeout(() => {
                setInternalOpen(true)
              }, 0)
            }
          }}
          className='absolute left-1/2 top-1/2 z-50 h-min w-72 -translate-x-1/2 -translate-y-1/2 rounded-2xl border-2 border-C/Color3 bg-C/Color1 p-4 text-left text-xs shadow-xl'
        >
          <Dialog.Title
            className='sr-only'
            aria-hidden
          >
            {mode === 'success' ? 'Se han guardado correctamente los cambios' : 'Desea guardar los cambios realizados en la tienda'}
          </Dialog.Title>
          <Dialog.Description
            className='sr-only'
            aria-hidden
          >
            {mode === 'success' ? 'Se han guardado correctamente los cambios' : 'Desea guardar los cambios realizados en la tienda'}
          </Dialog.Description>
          <div className='flex flex-col items-center justify-center gap-y-2'>
            <p className='text-center font-normal'>
              {message ? (
                message
              ) : mode === 'confirm' ? (
                <>
                  ¿{'Desea guardar los cambios realizados en la tienda'} <span className='font-semibold'>{nombre}</span>?
                </>
              ) : (
                <>
                  {'Se han guardado correctamente los cambios'} <span className='font-semibold'>{nombre}</span>
                </>
              )}
            </p>
            {mode === 'confirm' && (
              <div className='flex w-full justify-center gap-3'>
                <button
                  onClick={onConfirm}
                  className='flex cursor-pointer items-center rounded-xl border border-solid border-C/Color7 bg-C/Color3 px-3 text-xs font-normal text-C/Color1'
                >
                  {'Sí'}
                </button>
                <button
                  ref={noBtnRef}
                  onClick={handleCloseButton}
                  className='flex cursor-pointer items-center rounded-xl border border-C/Color3 bg-C/Color7 px-3 py-0.5 text-xs font-normal text-C/Color3'
                >
                  {'No'}
                </button>
              </div>
            )}
            {mode === 'success' && (
              <div className='flex w-full justify-center gap-3'>
                <button
                  onClick={handleCloseButton}
                  className='flex cursor-pointer items-center rounded-xl border border-C/Color3 bg-C/Color7 px-3 py-0.5 text-xs font-normal text-C/Color3'
                >
                  {'Ok'}
                </button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
})

ConfirmationSaveStore.displayName = 'ConfirmationSaveStore'

export default ConfirmationSaveStore
